import { useState } from "react";
import { weekRank, type RankItem } from "@/mocks/gossipData";

export default function WeekRank() {
  const [bar, setBar] = useState(false);
  const maxHeat = weekRank[0].heat;

  const trendLabel = (t: string) => {
    if (t === "up") return { text: "较上周上升", cls: "text-accent-600 bg-accent-100" };
    if (t === "down") return { text: "较上周下降", cls: "text-secondary-700 bg-secondary-100" };
    if (t === "new") return { text: "新上榜", cls: "text-primary-700 bg-primary-100" };
    return { text: "热度持平", cls: "text-foreground-500 bg-background-200" };
  };

  return (
    <section id="week" className="bg-background-100 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-12">
          <div>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-700">
              <i className="ri-bar-chart-2-fill text-accent-700"></i>
              WEEKLY · 每周榜单
            </span>
            <h2 className="mt-2 font-display text-3xl md:text-4xl text-foreground-950">
              本周排行<span className="text-accent-700">·</span>七天热度累积
            </h2>
            <p className="mt-3 max-w-2xl text-sm md:text-base text-foreground-600 leading-relaxed">
              把一周的瓜田脉络浓缩成一张总榜，看看谁是过去七天真正霸屏的爆款话题。
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setBar(false)}
              className={`whitespace-nowrap h-10 px-5 rounded-full text-sm font-semibold transition-colors ${!bar ? "bg-foreground-900 text-background-50" : "bg-background-200 text-foreground-600 hover:bg-background-300"}`}
            >
              列表视图
            </button>
            <button
              onClick={() => setBar(true)}
              className={`whitespace-nowrap h-10 px-5 rounded-full text-sm font-semibold transition-colors ${bar ? "bg-foreground-900 text-background-50" : "bg-background-200 text-foreground-600 hover:bg-background-300"}`}
            >
              柱状视图
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <ol className="rounded-2xl bg-background-50 ring-1 ring-background-200 divide-y divide-background-200 overflow-hidden">
              {weekRank.map((item: RankItem) => (
                <li key={item.rank} className="group flex items-center gap-4 px-4 md:px-6 py-4 hover:bg-background-100/60 transition-colors cursor-pointer">
                  <span className="w-8 text-center shrink-0">
                    <span
                      className={`inline-flex h-7 w-7 items-center justify-center rounded-lg text-sm font-bold ${item.rank === 1 ? "bg-accent-500 text-background-950" : item.rank <= 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"}`}
                    >
                      {item.rank}
                    </span>
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm md:text-[15px] font-medium text-foreground-950 leading-snug truncate">
                      {item.title}
                    </p>
                    <div className="mt-2.5 flex items-center gap-3">
                      {bar ? (
                        <div className="flex h-2.5 w-full max-w-md rounded-full bg-background-200 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${item.rank <= 3 ? "bg-accent-500" : "bg-accent-300"}`}
                            style={{ width: `${(item.heat / maxHeat) * 100}%` }}
                          ></div>
                        </div>
                      ) : (
                        <span className="text-xs text-foreground-500 whitespace-nowrap">{item.heatText}</span>
                      )}
                    </div>
                  </div>
                  <div className="hidden sm:flex flex-col items-end gap-1.5 shrink-0">
                    <span className="text-xs text-foreground-500 whitespace-nowrap">{item.heatText}</span>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] whitespace-nowrap ${trendLabel(item.trend).cls}`}>
                      <i className={`${item.trend === "up" ? "ri-arrow-up-line" : item.trend === "down" ? "ri-arrow-down-line" : item.trend === "new" ? "ri-sparkling-line" : "ri-subtract-line"} text-xs`}></i>
                      {trendLabel(item.trend).text}
                    </span>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <aside className="lg:col-span-4 space-y-6">
            <div className="rounded-2xl bg-foreground-950 p-6 text-background-50">
              <h3 className="text-base font-bold mb-1">本周热度峰值</h3>
              <p className="text-sm text-background-100/80">累计阅读量级</p>
              <p className="font-display text-4xl mt-3 text-accent-400">{weekRank[0].heatText}</p>
              <p className="mt-2 text-sm text-background-100/80 leading-relaxed">{weekRank[0].title}</p>
              <div className="mt-4 h-1.5 w-full rounded-full bg-background-700 overflow-hidden">
                <div className="h-full w-full rounded-full bg-accent-500"></div>
              </div>
            </div>
            <div className="rounded-2xl bg-background-50 ring-1 ring-background-200 p-5 md:p-6">
              <h3 className="text-base font-bold text-foreground-950 mb-4">榜单升降速览</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-foreground-700"><i className="ri-arrow-up-line text-accent-600"></i>上升话题</span>
                  <span className="font-bold text-foreground-950">{weekRank.filter((r) => r.trend === "up").length}</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-foreground-700"><i className="ri-arrow-down-line text-secondary-600"></i>回落话题</span>
                  <span className="font-bold text-foreground-950">{weekRank.filter((r) => r.trend === "down").length}</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-foreground-700"><i className="ri-sparkling-line text-primary-600"></i>新晋上榜</span>
                  <span className="font-bold text-foreground-950">{weekRank.filter((r) => r.trend === "new").length}</span>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}